import { datadogLogs } from "@datadog/browser-logs";
import { useContext } from "react";
import { setCookie, setLocalStorage } from "../layout/cookies/CookieFunctions";
import { findGetParameter, removeParam } from "../utilities/UtilityFunctions";
import { AuthContext } from "./BCPAuthProvider";

export function authReducer(authState, action) {
  switch (action.type) {
    case "processing":
      return {
        ...authState,
        processing: true,
        ...action.payload
      };
    case "setcookieconsent":
      return {
        ...authState,
        ...action.payload
      };

    case "initialise":
    case "signin":
      return {
        ...action.payload,
        processing: false
      };
    case "signout":
      return { processing: false };
    case "setislogin":
    case "setbcpevent":
      return {
        ...authState,
        ...action.payload
      };

    case "updatecart":
      return {
        ...authState,
        cart: action.payload
      };

    default:
      return {
        ...authState,
        processing: false
      };
  }
}

export function doProcessing(processing) {
  return {
    type: "processing",
    payload: { processing: processing }
  };
}

export function doSignin(signinParams) {
  return {
    type: "signin",
    payload: signinParams
  };
}

export function doSignout() {
  return {
    type: "signout"
  };
}

export function doUpdateCart(cart) {
  return {
    type: "updatecart",
    payload: cart
  };
}

export function setIsLogin(isLogin) {
  return {
    type: "setislogin",
    payload: { isLogin: isLogin }
  };
}

export function setCookieConsent(cookieConsent) {
  if (cookieConsent !== undefined) setCookie("CookieConsent", cookieConsent);

  if (cookieConsent?.analytical) {
    datadogLogs.setTrackingConsent("granted");
  } else {
    datadogLogs.setTrackingConsent("not-granted");
  }

  return {
    type: "setcookieconsent",
    payload: {
      cookieConsent:
        cookieConsent !== undefined
          ? cookieConsent
          : {
              essential: true,
              analytical: false,
              performance: false,
              informational: false
            }
    }
  };
}

export function setBcpEvent(event) {
  return {
    type: "setbcpevent",
    payload: {
      event: event || undefined
    }
  };
}

/**
 *
 * useSigning
 */
export function useSetIsLogin(isLogin) {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (isLogin) => {
    const redirectUrl = removeParam(
      `${findGetParameter("exchange_token") ? "exchange_token" : "code"}`,
      window.location.href
    );

    setLocalStorage("bcpRedirectUrl", redirectUrl);

    try {
      if (!context) return false;
      context.dispatch(setIsLogin(isLogin));
    } catch (e) {
      return false;
    }
  };
}

export function useSetCookieConsent(cookieConsent) {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (cookieConsent) => {
    try {
      if (!context) return false;
      context.dispatch(setCookieConsent(cookieConsent));
      return true;
    } catch (e) {
      return false;
    }
  };
}

export function useSetBcpEvent(event) {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (event) => {
    try {
      if (!context) return false;
      context.dispatch(setBcpEvent(event));
      return true;
    } catch (e) {
      return false;
    }
  };
}

export function useProcessing(processing) {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (processing) => {
    try {
      if (!context) return false;
      context.dispatch(
        doProcessing(processing !== undefined ? processing : true)
      );
      return true;
    } catch (e) {
      return false;
    }
  };
}

export function useSignin(singinConfig) {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (singinConfig) => {
    if (!context) return false;
    context.dispatch(doSignin(singinConfig));
    return true;
  };
}

export function useSignout() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return () => {
    try {
      if (!context) return false;
      context.dispatch(doSignout());
      return true;
    } catch (e) {
      return false;
    }
  };
}

export function useUpdateCart(cart) {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth Provider is missing. Please add the AuthProvider before Router"
    );
  }

  return (cart) => {
    try {
      if (!context) return false;
      context.dispatch(doUpdateCart(cart));
      return true;
    } catch (e) {
      return false;
    }
  };
}
